import React, { FC, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';
import _groupBy from 'lodash/groupBy';
import { SCREEN_NAMES, SkillsHelper } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import { AnchorButton, UserInfoContainer, InfoHeading } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

import {
  UserInfoSection,
  UserInfoHeadingContainer,
  UserInfoHeading,
  StyledIcon,
  SkillsList,
  RowDivContainer,
  StylesSkillSubHeading,
  StyledPill,
  StyledIconPill,
  PillsContainer,
  UserInfoBtnContainer,
  CategoryName,
} from './Styles';

const { useSkillText } = SkillsHelper;

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const sectionName = 'skills';

const Skills: FC = () => {
  const { t } = useTranslation();
  const context = useContext(ProfileViewContext);
  const [openSection, setOpenSection] = useState(true);
  const [mediaViewerIsOpen, setMediaViewerIsOpen] = useState(false);
  const [mediaList, setMediaList] = useState([]);

  const getSkillText = useSkillText();

  const {
    isPreview,
    fetcher: { profile },
    isAdmin,
    onEditClick,
  } = context;

  const getIconName = (category): string =>
    `skills-assets-${category
      .toLowerCase()
      .replace(/\s*[&,]\s*/g, ' ')
      .replace(/\s+/g, '-')}`;

  const getKey = (name: string, index: number | string): string => `${name}${index}`;

  const createSkillsMap = () => {
    const { skills } = profile;
    let skillCategoryMap = {};
    const filteredSkills = skills.filter((s) => s.parentObject);

    if (!_isEmpty(filteredSkills)) {
      skillCategoryMap = _groupBy(filteredSkills, (skill) => {
        return skill.parentObject.parentObject ? skill.parentObject.parentObject.code : skill.parentObject.code;
      });

      Object.keys(skillCategoryMap).forEach((keyName) => {
        const skillByCategory = skillCategoryMap[keyName];

        if (skillByCategory[0].parentObject.parentObject) {
          skillCategoryMap[keyName] = _groupBy(skillByCategory, (e) => e.parentObject.code);
        }
      });
    }

    return skillCategoryMap;
  };

  const mediaViewerClose = (): void => setMediaViewerIsOpen(false);

  const openSkillsMedia = (mediaId: number): void => {
    const newMediaList = context.fetcher.profile.mediaAssets?.filter(
      (asset): boolean => mediaId === asset.profileMediaId
    );

    setMediaList(newMediaList);
    setMediaViewerIsOpen(true);
  };

  const skillsMap = createSkillsMap();
  const icons = ['media-play-circle'];

  const renderSkillPills = (category: string, subCategory?: string): JSX.Element => {
    if (_isEmpty(skillsMap)) return null;

    const skills = _isArray(skillsMap[category]) ? skillsMap[category] : skillsMap[category][subCategory];

    return skills.map((skill) =>
      skill.profileMediaId ? (
        <StyledIconPill
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-name`}
          rightIcons={icons}
          text={skill.name}
          key={skill.id}
          onClick={(): void => openSkillsMedia(skill.profileMediaId)}
        />
      ) : (
        <StyledPill data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-name`} text={skill.name} key={skill.id} />
      )
    );
  };

  const previewSkillsButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      onClick={(): void => onEditClick('skills-edit')}
    >
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      href={`/profile/${profile.profileId}/skills-edit`}
    >
      {t('common:button.edit')}
    </DuoLink>
  );
  const commonSkillsButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      onClick={(): void => onEditClick('skills-add')}
    >
      <Button outlined="true">{t('common:profile.skills.addSkills')}</Button>
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      href={`/profile/${profile.profileId}/skills-add`}
    >
      <Button outlined="true">{t('common:profile.skills.addSkills')}</Button>
    </DuoLink>
  );
  const previewIcon = openSection === true ? 'chevron-open-thick' : 'chevron-closed-thick';

  return (
    <UserInfoContainer isPreview={isPreview} id="div-skills" data-testid="profile-skills-container">
      {!_isEmpty(skillsMap) ? (
        <UserInfoSection>
          <UserInfoHeadingContainer isPreview={isPreview}>
            <UserInfoHeading isPreview={isPreview}>{t('common:profile.nav.skills')}</UserInfoHeading>
            {!isPreview && previewSkillsButton}
            {isPreview && (
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${previewIcon}-icon`}
                name={previewIcon}
                onClick={(): void => setOpenSection(!openSection)}
              />
            )}
          </UserInfoHeadingContainer>
          {openSection && (
            <SkillsList>
              {Object.keys(skillsMap).map((category, index) => (
                <RowDivContainer key={getKey('row', index)}>
                  <StylesSkillSubHeading>
                    <Icon
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-category-icon`}
                      name={getIconName(category)}
                    />
                    <CategoryName data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-category`}>
                      {getSkillText(category)}
                    </CategoryName>
                  </StylesSkillSubHeading>
                  {skillsMap[category].length > 0 ? (
                    <div key={getKey('subcategory', category)}>
                      <PillsContainer>{renderSkillPills(category)}</PillsContainer>
                    </div>
                  ) : (
                    <>
                      {Object.keys(skillsMap[category]).map((subCategory, subCategoryIndex) => (
                        <div key={getKey('subcategory', subCategoryIndex)}>
                          <StylesSkillSubHeading
                            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-sub-category`}
                          >
                            {' '}
                            {getSkillText(subCategory)}
                          </StylesSkillSubHeading>
                          <PillsContainer>{renderSkillPills(category, subCategory)}</PillsContainer>
                        </div>
                      ))}
                    </>
                  )}
                </RowDivContainer>
              ))}
            </SkillsList>
          )}
        </UserInfoSection>
      ) : (
        !isPreview && (
          <>
            <InfoHeading>{t('common:profile.nav.skills')}</InfoHeading>
            <UserInfoBtnContainer>{commonSkillsButton}</UserInfoBtnContainer>
          </>
        )
      )}
      {mediaViewerIsOpen && <CloudinaryMediaViewer isSmall mediaList={mediaList} handleCloseModal={mediaViewerClose} />}
    </UserInfoContainer>
  );
};

export default Skills;
